import type { PeerId, SegmentId } from "@p2p-hls/protocol";
import type { PeerRegistry } from "./peer-registry.js";
import type { SegmentStore } from "./segment-store.js";
import type { EngineMetrics } from "./metrics.js";
import type { DeliverySource, EngineConfig, SegmentResult } from "./types.js";

/** Fetches a segment over HTTP; abortable so a P2P win can cancel the hedge. */
export type HttpFetch = (url: string, signal?: AbortSignal) => Promise<Uint8Array>;

/**
 * The scheduling brain. For each segment the player needs, it decides between a
 * nearest peer and the CDN, honoring the play deadline so P2P never causes a
 * rebuffer:
 *
 *   1. Already cached      -> return instantly.
 *   2. Deadline too close  -> HTTP only (no time to gamble on a peer).
 *   3. A capable peer      -> request from it AND arm a hedged HTTP request at a
 *                             fraction of the remaining deadline; first success
 *                             wins, the loser is cancelled.
 *   4. No peer has it      -> HTTP.
 *
 * Whatever path delivers the bytes, we cache them and announce HAVE so this peer
 * can now serve others (that is where CDN offload actually comes from).
 */
export class SegmentScheduler {
  constructor(
    private readonly config: EngineConfig,
    private readonly registry: PeerRegistry,
    private readonly store: SegmentStore,
    private readonly metrics: EngineMetrics,
    /** Announce newly-held / dropped segments to connected peers. */
    private readonly announce: (add: SegmentId[], drop: SegmentId[]) => void,
  ) {}

  async getSegment(seg: SegmentId, deadlineMs: number, httpFetch: HttpFetch): Promise<SegmentResult> {
    const start = now();

    const cached = this.store.get(seg);
    if (cached) {
      this.record("cache", cached.byteLength, 0, deadlineMs, null);
      return { seg, data: cached, source: "cache", fromPeer: null, elapsedMs: 0 };
    }

    const picked =
      deadlineMs >= this.config.httpOnlyDeadlineMs
        ? this.registry.pick(seg, this.config.peerRequestConcurrency)
        : null;
    // Absolute-RTT gate: if the best peer is MEASURABLY too slow, skip P2P and go
    // straight to CDN (cuts wasted deadline on weak links). Unprobed peers
    // (rtt = Infinity) still get one try — the hedge race protects them. 0 = off.
    const maxRtt = this.config.maxPeerRttMs;
    const peer = picked && maxRtt > 0 && Number.isFinite(picked.rtt()) && picked.rtt() > maxRtt ? null : picked;

    if (!peer) {
      const data = await httpFetch(seg.url);
      this.onDelivered(seg, data);
      const elapsedMs = now() - start;
      this.record("http", data.byteLength, elapsedMs, deadlineMs, null);
      return { seg, data, source: "http", fromPeer: null, elapsedMs };
    }

    return this.race(seg, deadlineMs, httpFetch, start);
  }

  /**
   * Download a segment across up to `maxSwarmPeers` peers in parallel (each
   * serves a distinct chunk stride), aggregating the swarm's upload. Falls back
   * to a single peer, and re-requests a failed stride from a healthy peer once.
   */
  private async swarmFetch(seg: SegmentId): Promise<{ data: Uint8Array; fromPeer: string }> {
    const timeout = this.config.peerRequestTimeoutMs;
    const peers = this.registry.peersWith(seg, this.config.maxSwarmPeers, this.config.peerRequestConcurrency);
    if (peers.length === 0) throw new Error("no peer has segment");
    if (peers.length === 1) {
      return { data: await peers[0]!.request(seg, timeout), fromPeer: peers[0]!.id };
    }

    const k = peers.length;
    const chunks = new Map<number, Uint8Array>();
    let total = -1;
    const failedOffsets: number[] = [];
    const results = await Promise.allSettled(peers.map((p, i) => p.requestStrided(seg, k, i, timeout)));
    results.forEach((r, i) => {
      if (r.status === "fulfilled") {
        total = r.value.total;
        for (const [idx, ch] of r.value.entries) chunks.set(idx, ch);
      } else {
        failedOffsets.push(i);
      }
    });
    if (total < 0) throw new Error("swarm: all peers failed");

    // Re-request any failed strides from a peer that succeeded.
    if (failedOffsets.length) {
      const healthy = peers.find((_, i) => results[i]!.status === "fulfilled")!;
      const retries = await Promise.all(failedOffsets.map((off) => healthy.requestStrided(seg, k, off, timeout)));
      for (const r of retries) for (const [idx, ch] of r.entries) chunks.set(idx, ch);
    }
    if (chunks.size !== total) throw new Error(`swarm: incomplete ${chunks.size}/${total}`);

    const ordered = [...chunks.entries()].sort((a, b) => a[0] - b[0]).map((e) => e[1]);
    const data = concat(ordered);
    return { data, fromPeer: "swarm" };
  }

  private race(
    seg: SegmentId,
    remaining: number,
    httpFetch: HttpFetch,
    start: number,
  ): Promise<SegmentResult> {
    return new Promise<SegmentResult>((resolve, reject) => {
      let settled = false;
      let peerFailed = false;
      let httpStarted = false;
      const abort = new AbortController();

      const startHttp = (hedged: boolean) => {
        if (httpStarted || settled) return;
        httpStarted = true;
        httpFetch(seg.url, abort.signal)
          .then((data) => {
            if (settled) return;
            settled = true;
            clearTimeout(hedgeTimer);
            this.onDelivered(seg, data);
            const source: DeliverySource = hedged ? "http-hedge-win" : "http";
            const elapsedMs = now() - start;
            this.record(source, data.byteLength, elapsedMs, remaining, null);
            resolve({ seg, data, source, fromPeer: null, elapsedMs });
          })
          .catch((err: unknown) => {
            if (settled) return;
            if (peerFailed) {
              settled = true;
              clearTimeout(hedgeTimer);
              reject(err instanceof Error ? err : new Error(String(err)));
            }
          });
      };

      this.swarmFetch(seg)
        .then(({ data, fromPeer }) => {
          if (settled) return;
          settled = true;
          clearTimeout(hedgeTimer);
          abort.abort(); // cancel any hedged HTTP — P2P won
          this.onDelivered(seg, data);
          const elapsedMs = now() - start;
          this.record("p2p", data.byteLength, elapsedMs, remaining, fromPeer);
          resolve({ seg, data, source: "p2p", fromPeer, elapsedMs });
        })
        .catch(() => {
          peerFailed = true;
          startHttp(false); // peers failed -> go to CDN immediately
        });

      const hedgeTimer = setTimeout(
        () => startHttp(true),
        Math.max(0, remaining * this.config.hedgeAtDeadlineFraction),
      );
    });
  }

  /**
   * Prefetch a future segment from a peer that already has it — P2P ONLY (no CDN
   * fallback; prefetching from the CDN would defeat the purpose). Returns true if
   * it was pulled and cached. Used by the engine's prefetch-ahead logic to make
   * LIVE P2P work when viewers are only slightly out of sync.
   */
  async prefetch(seg: SegmentId): Promise<boolean> {
    if (this.store.get(seg)) return false;
    // Reserve ≥1 peer slot for latency-critical playback requests.
    const peer = this.registry.pick(seg, Math.max(1, this.config.peerRequestConcurrency - 1));
    if (!peer) return false;
    const start = now();
    try {
      const data = await peer.request(seg, this.config.peerRequestTimeoutMs);
      this.onDelivered(seg, data);
      // Deadline = Infinity: a prefetch is ahead of playback, never a "miss".
      this.record("p2p", data.byteLength, now() - start, Infinity, peer.id);
      return true;
    } catch {
      return false;
    }
  }

  /** Cache the bytes and announce HAVE so this peer can now serve them onward. */
  private onDelivered(seg: SegmentId, data: Uint8Array): void {
    const dropped = this.store.put(seg, data);
    this.announce([seg], dropped);
  }

  private record(
    source: DeliverySource,
    bytes: number,
    elapsedMs: number,
    deadlineMs: number,
    fromPeer: PeerId | null,
  ): void {
    this.metrics.recordDelivery({ source, bytes, elapsedMs, deadlineMs, fromPeer });
  }
}

function now(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

function concat(chunks: Uint8Array[]): Uint8Array {
  const total = chunks.reduce((n, c) => n + c.byteLength, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const c of chunks) {
    out.set(c, off);
    off += c.byteLength;
  }
  return out;
}
